import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './EditVersionTimeline/testIds';

export type EditVersionEntry = {
  id: string;
  kind: 'baseline' | 'edit';
  // Only reliable once the per-edit history table exists.
  savedAtIso?: string;
  editedSegmentCount?: number;
  isCurrent: boolean;
};

function TypeCell({ kind }: { kind: EditVersionEntry['kind'] }) {
  const isEdit = kind === 'edit';
  return (
    <HStack spacing={1.5} w={pxToRem(52)} flexShrink={0}>
      {isEdit ? (
        <Box boxSize={pxToRem(7)} borderRadius="full" bg="blue.500" flexShrink={0} aria-hidden />
      ) : (
        <Box
          boxSize={pxToRem(8)}
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.400"
          flexShrink={0}
          aria-hidden
        />
      )}
      <Text fontSize="xs" fontWeight="semibold" color={isEdit ? 'blue.500' : 'fg.muted'}>
        {isEdit ? <Trans>Edit</Trans> : <Trans>Auto</Trans>}
      </Text>
    </HStack>
  );
}

function EntryDetail({
  entry,
  publishedVersion,
}: {
  entry: EditVersionEntry;
  publishedVersion: number | null;
}) {
  if (entry.kind === 'baseline') {
    if (publishedVersion == null) {
      return null;
    }
    return (
      <Text fontSize="xs" color="fg.muted" noOfLines={1}>
        <Trans>Generated with Published v{publishedVersion}</Trans>
      </Text>
    );
  }

  const savedAt = entry.savedAtIso != null ? djs(entry.savedAtIso).format('D MMM, h:mma') : null;
  if (entry.editedSegmentCount == null && savedAt == null) {
    return null;
  }

  return (
    <Text fontSize="xs" color="fg.muted" noOfLines={1}>
      {entry.editedSegmentCount != null ? (
        <Plural
          value={entry.editedSegmentCount}
          one="# segment changed"
          other="# segments changed"
        />
      ) : null}
      {entry.editedSegmentCount != null && savedAt != null ? ' · ' : null}
      {savedAt}
    </Text>
  );
}

function CurrentEntry({
  entry,
  publishedVersion,
}: {
  entry: EditVersionEntry;
  publishedVersion: number | null;
}) {
  const title =
    entry.kind === 'baseline' ? (
      <Trans>Auto-translated baseline</Trans>
    ) : (
      <Trans>Current edit</Trans>
    );

  return (
    <Box
      px={3}
      py={2}
      borderBottomWidth="1px"
      borderColor="border.default"
      data-testid={testIds.currentEntry}
      data-current="true"
    >
      <HStack spacing={2} align="center">
        <TypeCell kind={entry.kind} />
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color="fg.default"
          noOfLines={1}
          flex="1"
          minW={0}
        >
          {title}
        </Text>
        <HStack spacing={1} flexShrink={0} data-testid={testIds.liveMarker}>
          <Box boxSize={pxToRem(6)} borderRadius="full" bg="green.500" aria-hidden />
          <Text fontSize="xs" fontWeight="semibold" color="green.600">
            <Trans>Live</Trans>
          </Text>
        </HStack>
      </HStack>
      <Box pl={pxToRem(60)} mt={0.5}>
        <EntryDetail entry={entry} publishedVersion={publishedVersion} />
      </Box>
    </Box>
  );
}

// Only the baseline is restorable; earlier edits have no per-edit history yet.
function PriorEntry({
  entry,
  publishedVersion,
  onRestoreBaseline,
  isRestoring,
}: {
  entry: EditVersionEntry;
  publishedVersion: number | null;
  onRestoreBaseline?: () => void;
  isRestoring: boolean;
}) {
  const canRestore = entry.kind === 'baseline' && onRestoreBaseline != null;
  return (
    <HStack
      role="group"
      spacing={2}
      align="center"
      px={3}
      py={2}
      borderBottomWidth="1px"
      borderColor="border.default"
      _last={{ borderBottomWidth: 0 }}
      _hover={{ bg: 'bg.subtle' }}
      data-testid={entry.kind === 'baseline' ? testIds.baselineEntry : testIds.entry}
    >
      <TypeCell kind={entry.kind} />
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight="medium" color="fg.default" noOfLines={1}>
          {entry.kind === 'baseline' ? (
            <Trans>Auto-translated baseline</Trans>
          ) : (
            <Trans>Earlier edit</Trans>
          )}
        </Text>
        <EntryDetail entry={entry} publishedVersion={publishedVersion} />
      </Box>
      {canRestore ? (
        <Button
          size="xs"
          variant="secondary"
          flexShrink={0}
          opacity={isRestoring ? 1 : 0}
          isLoading={isRestoring}
          _groupHover={{ opacity: 1 }}
          _groupFocusWithin={{ opacity: 1 }}
          onClick={() => onRestoreBaseline()}
          data-testid={testIds.restore}
        >
          <Trans>Restore</Trans>
        </Button>
      ) : null}
    </HStack>
  );
}

export function EditVersionTimeline({
  entries,
  publishedVersion,
  languageLabel,
  onRestoreBaseline,
  isRestoring = false,
}: {
  // Newest first, baseline last.
  entries: EditVersionEntry[];
  publishedVersion: number | null;
  languageLabel?: string;
  // Reverts the whole language to the auto-translated baseline.
  onRestoreBaseline?: () => void;
  isRestoring?: boolean;
}) {
  const current = entries.find((entry) => entry.isCurrent);
  const prior = entries.filter((entry) => !entry.isCurrent);

  return (
    <Box w={pxToRem(320)} maxW="full">
      <Box px={3} py={2} borderBottomWidth="1px" borderColor="border.default">
        <Text fontSize="sm" fontWeight="semibold" data-testid={testIds.heading}>
          {languageLabel == null ? (
            <Trans>Edit history</Trans>
          ) : (
            <Trans>Edit history · {languageLabel}</Trans>
          )}
        </Text>
      </Box>

      {current != null ? (
        <CurrentEntry entry={current} publishedVersion={publishedVersion} />
      ) : null}

      {prior.length > 0 ? (
        <VStack
          align="stretch"
          spacing={0}
          maxH={pxToRem(280)}
          overflowY="auto"
          aria-label={t`Earlier edits`}
        >
          {prior.map((entry) => (
            <PriorEntry
              key={entry.id}
              entry={entry}
              publishedVersion={publishedVersion}
              onRestoreBaseline={onRestoreBaseline}
              isRestoring={isRestoring}
            />
          ))}
        </VStack>
      ) : null}
    </Box>
  );
}
